/* ==========================================================================
   Accessibility Widget — KOTT Lab
   Persists settings via localStorage. Keyboard-friendly.
   ========================================================================== */
(function () {
  'use strict';

  var STORAGE_KEY = 'kott_a11y_v1';
  var html = document.documentElement;

  var defaultState = {
    fontSize: 0,
    letterSpacing: false,
    lineHeight: false,
    highlightLinks: false,
    highlightHeadings: false,
    readableFont: false,
    noMotion: false,
    contrast: '',
    grayscale: false,
    bigCursor: false,
    readingGuide: false
  };

  var state = loadState();

  function loadState() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return Object.assign({}, defaultState);
      return Object.assign({}, defaultState, JSON.parse(raw));
    } catch (e) {
      return Object.assign({}, defaultState);
    }
  }

  function saveState() {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) {}
  }

  function applyState() {
    var body = document.body;
    var prevFontSize = html.getAttribute('data-a11y-font-size');
    var nextFontSize = state.fontSize === 0 ? null : String(state.fontSize);

    if (nextFontSize === null) {
      html.removeAttribute('data-a11y-font-size');
    } else {
      html.setAttribute('data-a11y-font-size', nextFontSize);
    }

    var classMap = {
      'a11y-letter-spacing':   state.letterSpacing,
      'a11y-line-height':      state.lineHeight,
      'a11y-links':            state.highlightLinks,
      'a11y-headings':         state.highlightHeadings,
      'a11y-readable-font':    state.readableFont,
      'a11y-no-motion':        state.noMotion,
      'a11y-grayscale':        state.grayscale,
      'a11y-big-cursor':       state.bigCursor,
      'a11y-reading-guide-on': state.readingGuide
    };
    Object.keys(classMap).forEach(function (cls) {
      body.classList.toggle(cls, !!classMap[cls]);
    });

    body.classList.remove('a11y-mono', 'a11y-dark', 'a11y-light');
    if (state.contrast === 'mono') body.classList.add('a11y-mono');
    if (state.contrast === 'dark') body.classList.add('a11y-dark');
    if (state.contrast === 'light') body.classList.add('a11y-light');

    /* Keep the global reduced-motion signal in sync so JS-driven animations
       (WebGL/scroll/rAF loops) actually stop. The boot script in theme.liquid
       defines __kottSetReduceMotion; it OR-combines with the OS preference. */
    if (typeof window.__kottSetReduceMotion === 'function') {
      window.__kottSetReduceMotion(state.noMotion);
    }

    syncButtons();

    if (prevFontSize !== nextFontSize) {
      window.requestAnimationFrame(function () {
        if (typeof window.kottRecalcHeaderHeights === 'function') {
          window.kottRecalcHeaderHeights();
        }
        window.dispatchEvent(new Event('resize'));
      });
    }
  }

  function t(key, fallback) {
    var labels = window.KottA11yLabels || {};
    return labels[key] || fallback;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c];
    });
  }

  function buildWidget() {
    if (document.querySelector('.a11y-trigger')) return;

    var trigger = document.createElement('button');
    trigger.className = 'a11y-trigger';
    trigger.type = 'button';
    trigger.setAttribute('aria-label', t('open', 'Open accessibility menu'));
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', 'kott-a11y-panel');
    trigger.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true">'
      + '<circle cx="12" cy="4.5" r="2.1" fill="currentColor"/>'
      + '<path fill="currentColor" d="M5.5 9.25h13c.55 0 1 .45 1 1s-.45 1-1 1H14v3.05l1.78 6.5c.16.55-.18 1.13-.73 1.29-.55.16-1.13-.18-1.29-.73L12 14.5l-1.76 5.86c-.16.55-.74.89-1.29.73-.55-.16-.89-.74-.73-1.29l1.78-6.5V11.25H5.5c-.55 0-1-.45-1-1s.45-1 1-1z"/>'
      + '</svg>';

    var panel = document.createElement('div');
    panel.className = 'a11y-panel';
    panel.id = 'kott-a11y-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'false');
    panel.setAttribute('aria-labelledby', 'kott-a11y-title');
    panel.setAttribute('aria-hidden', 'true');

    var title          = escapeHtml(t('title',           'Accessibility menu'));
    var closeLabel     = escapeHtml(t('close',           'Close'));
    var grpText        = escapeHtml(t('text',            'Text size'));
    var grpReading     = escapeHtml(t('reading',         'Reading'));
    var grpColor       = escapeHtml(t('color',           'Colors and contrast'));
    var grpNav         = escapeHtml(t('navigation',      'Navigation and orientation'));
    var fontRegular    = escapeHtml(t('font_regular',    'Regular'));
    var fontLarge      = escapeHtml(t('font_large',      'Large'));
    var fontLarger     = escapeHtml(t('font_larger',     'Larger'));
    var letterSpacing  = escapeHtml(t('letter_spacing',  'Letter spacing'));
    var lineHeight     = escapeHtml(t('line_height',     'Line spacing'));
    var readableFont   = escapeHtml(t('readable_font',   'Readable font'));
    var highlightLinks = escapeHtml(t('highlight_links', 'Highlight links'));
    var highlightHead  = escapeHtml(t('highlight_headings','Highlight headings'));
    var readingGuide   = escapeHtml(t('reading_guide',   'Reading guide'));
    var monoLabel      = escapeHtml(t('contrast_mono',   'Black and white'));
    var darkLabel      = escapeHtml(t('contrast_dark',   'Dark contrast'));
    var lightLabel     = escapeHtml(t('contrast_light',  'Light contrast'));
    var grayscale      = escapeHtml(t('grayscale',       'Grayscale'));
    var bigCursor      = escapeHtml(t('big_cursor',      'Large cursor'));
    var noMotion       = escapeHtml(t('no_motion',       'Stop animations'));
    var reset          = escapeHtml(t('reset',           'Reset settings'));
    var statementLink  = escapeHtml(t('statement_link',  'Full accessibility statement'));
    // Empty string = no statement in this language (the footer link is hidden too).
    var statementUrl   = (typeof window.KottA11yStatementUrl === 'string') ? window.KottA11yStatementUrl : '/pages/accessibility';
    var sampleGlyph    = escapeHtml(t('sample_glyph',    'A'));

    panel.innerHTML = [
      '<div class="a11y-panel__header">',
        '<h2 class="a11y-panel__title" id="kott-a11y-title">' + title + '</h2>',
        '<button class="a11y-panel__close" type="button" aria-label="' + closeLabel + '">',
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>',
        '</button>',
      '</div>',
      '<div class="a11y-panel__body">',

        '<div class="a11y-group">',
          '<h3 class="a11y-group__title">' + grpText + '</h3>',
          '<div class="a11y-options a11y-options--three">',
            '<button type="button" class="a11y-option a11y-option--font" data-fontsize="0">',
              '<span class="a11y-option__sample" aria-hidden="true">' + sampleGlyph + '</span>',
              '<span class="a11y-option__label">' + fontRegular + '</span>',
            '</button>',
            '<button type="button" class="a11y-option a11y-option--font" data-fontsize="1">',
              '<span class="a11y-option__sample" aria-hidden="true">' + sampleGlyph + '</span>',
              '<span class="a11y-option__label">' + fontLarge + '</span>',
            '</button>',
            '<button type="button" class="a11y-option a11y-option--font" data-fontsize="2">',
              '<span class="a11y-option__sample" aria-hidden="true">' + sampleGlyph + '</span>',
              '<span class="a11y-option__label">' + fontLarger + '</span>',
            '</button>',
          '</div>',
          '<div class="a11y-options" style="margin-top:8px">',
            '<button type="button" class="a11y-option" data-toggle="letterSpacing">' + letterSpacing + '</button>',
            '<button type="button" class="a11y-option" data-toggle="lineHeight">' + lineHeight + '</button>',
          '</div>',
        '</div>',

        '<div class="a11y-group">',
          '<h3 class="a11y-group__title">' + grpReading + '</h3>',
          '<div class="a11y-options">',
            '<button type="button" class="a11y-option" data-toggle="readableFont">' + readableFont + '</button>',
            '<button type="button" class="a11y-option" data-toggle="highlightLinks">' + highlightLinks + '</button>',
            '<button type="button" class="a11y-option" data-toggle="highlightHeadings">' + highlightHead + '</button>',
            '<button type="button" class="a11y-option" data-toggle="readingGuide">' + readingGuide + '</button>',
          '</div>',
        '</div>',

        '<div class="a11y-group">',
          '<h3 class="a11y-group__title">' + grpColor + '</h3>',
          '<div class="a11y-options">',
            '<button type="button" class="a11y-option" data-contrast="mono">' + monoLabel + '</button>',
            '<button type="button" class="a11y-option" data-contrast="dark">' + darkLabel + '</button>',
            '<button type="button" class="a11y-option" data-contrast="light">' + lightLabel + '</button>',
            '<button type="button" class="a11y-option" data-toggle="grayscale">' + grayscale + '</button>',
          '</div>',
        '</div>',

        '<div class="a11y-group">',
          '<h3 class="a11y-group__title">' + grpNav + '</h3>',
          '<div class="a11y-options">',
            '<button type="button" class="a11y-option" data-toggle="bigCursor">' + bigCursor + '</button>',
            '<button type="button" class="a11y-option" data-toggle="noMotion">' + noMotion + '</button>',
          '</div>',
        '</div>',

        '<div class="a11y-group">',
          '<button type="button" class="a11y-reset">' + reset + '</button>',
        '</div>',
      '</div>',

      statementUrl
        ? '<div class="a11y-panel__footer"><a href="' + statementUrl + '">' + statementLink + '</a></div>'
        : ''
    ].join('');

    var guide = document.createElement('div');
    guide.className = 'a11y-reading-guide';
    guide.setAttribute('aria-hidden', 'true');

    document.body.appendChild(trigger);
    document.body.appendChild(panel);
    document.body.appendChild(guide);

    /* legacy storage cleanup */
    try { localStorage.removeItem('kott_fab_dock_y'); } catch (e) {}

    /* Default positions: a11y at the bottom inline-end corner (bottom-left in
       RTL, bottom-right in LTR), WhatsApp stacked above it. Saved positions
       stay physical ({left, top}) so they keep working in every language. */
    function defaultLeft() {
      if (document.documentElement.dir === 'ltr') {
        return (document.documentElement.clientWidth || window.innerWidth) - 46 - 20;
      }
      return 20;
    }
    initDraggable(trigger, 'kott_a11y_trigger_pos', function () {
      return { left: defaultLeft(), top: window.innerHeight - 46 - 20 };
    });

    var wa = document.querySelector('.floating-whatsapp');
    if (wa) {
      initDraggable(wa, 'kott_whatsapp_pos', function () {
        return { left: defaultLeft(), top: window.innerHeight - 46 - 20 - 46 - 10 };
      });
    }

    bindEvents(trigger, panel, guide);
  }

  function initDraggable(el, storageKey, defaultPosFn) {
    var saved = null;
    try {
      var raw = localStorage.getItem(storageKey);
      if (raw) saved = JSON.parse(raw);
    } catch (e) {}
    var usingDefault = !saved;
    var pos = saved || defaultPosFn();
    pos = clampToViewport(el, pos.left, pos.top);
    applyPos(el, pos.left, pos.top);

    /* Block native link/image drag (esp. for <a> tags) */
    el.setAttribute('draggable', 'false');
    el.addEventListener('dragstart', function (e) { e.preventDefault(); });

    var dragging = false;
    var moved = false;
    var startX = 0;
    var startY = 0;
    var startLeft = 0;
    var startTop = 0;

    el.addEventListener('pointerdown', function (e) {
      if (e.button != null && e.button !== 0) return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      startY = e.clientY;
      var r = el.getBoundingClientRect();
      startLeft = r.left;
      startTop = r.top;
      try { el.setPointerCapture(e.pointerId); } catch (err) {}
    });

    el.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      var dy = e.clientY - startY;
      if (!moved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
        moved = true;
        el.classList.add('is-dragging');
      }
      if (!moved) return;
      var c = clampToViewport(el, startLeft + dx, startTop + dy);
      applyPos(el, c.left, c.top);
    });

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      if (moved) {
        usingDefault = false;
        el.classList.remove('is-dragging');
        try {
          localStorage.setItem(storageKey, JSON.stringify({
            left: parseInt(el.style.left, 10),
            top: parseInt(el.style.top, 10)
          }));
        } catch (err) {}
      }
    }

    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);

    /* Suppress the click that follows a drag */
    el.addEventListener('click', function (e) {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    }, true);

    window.addEventListener('resize', function () {
      var r = el.getBoundingClientRect();
      /* In LTR the default spot is measured from the right edge, so an
         undragged button has to follow the edge when the window resizes. */
      if (usingDefault && document.documentElement.dir === 'ltr') {
        var d = defaultPosFn();
        r = { left: d.left, top: r.top };
      }
      var c = clampToViewport(el, r.left, r.top);
      applyPos(el, c.left, c.top);
    });
  }

  function clampToViewport(el, left, top) {
    var w = el.offsetWidth || 46;
    var h = el.offsetHeight || 46;
    var min = 8;
    var maxLeft = window.innerWidth - w - min;
    var maxTop  = window.innerHeight - h - min;
    return {
      left: Math.max(min, Math.min(maxLeft, left)),
      top:  Math.max(min, Math.min(maxTop,  top))
    };
  }

  function applyPos(el, left, top) {
    el.style.left = left + 'px';
    el.style.top = top + 'px';
    el.style.right = 'auto';
    el.style.bottom = 'auto';
  }

  function bindEvents(trigger, panel, guide) {
    var closeBtn = panel.querySelector('.a11y-panel__close');
    var resetBtn = panel.querySelector('.a11y-reset');

    function open() {
      panel.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
      var firstFocusable = panel.querySelector('button, [href]');
      if (firstFocusable) firstFocusable.focus();
    }
    function close() {
      panel.setAttribute('aria-hidden', 'true');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus();
    }

    trigger.addEventListener('click', function () {
      var isOpen = panel.getAttribute('aria-hidden') === 'false';
      isOpen ? close() : open();
    });

    closeBtn.addEventListener('click', close);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && panel.getAttribute('aria-hidden') === 'false') {
        close();
      }
    });

    document.addEventListener('click', function (e) {
      var isOpen = panel.getAttribute('aria-hidden') === 'false';
      if (!isOpen) return;
      if (panel.contains(e.target) || trigger.contains(e.target)) return;
      close();
    });

    panel.addEventListener('click', function (e) {
      var btn = e.target.closest('button');
      if (!btn || btn === closeBtn || btn === resetBtn) return;

      var toggle  = btn.getAttribute('data-toggle');
      var contrast = btn.getAttribute('data-contrast');
      var fontsize = btn.getAttribute('data-fontsize');

      if (toggle && state.hasOwnProperty(toggle)) {
        state[toggle] = !state[toggle];
      } else if (contrast !== null) {
        state.contrast = (state.contrast === contrast) ? '' : contrast;
      } else if (fontsize !== null) {
        state.fontSize = parseInt(fontsize, 10);
      } else {
        return;
      }

      saveState();
      applyState();
    });

    resetBtn.addEventListener('click', function () {
      state = Object.assign({}, defaultState);
      saveState();
      applyState();
    });

    document.addEventListener('mousemove', function (e) {
      if (!state.readingGuide) return;
      guide.style.top = e.clientY + 'px';
    });
  }

  function syncButtons() {
    var panel = document.querySelector('.a11y-panel');
    if (!panel) return;

    panel.querySelectorAll('[data-toggle]').forEach(function (btn) {
      var key = btn.getAttribute('data-toggle');
      btn.setAttribute('aria-pressed', state[key] ? 'true' : 'false');
    });

    panel.querySelectorAll('[data-contrast]').forEach(function (btn) {
      var key = btn.getAttribute('data-contrast');
      btn.setAttribute('aria-pressed', state.contrast === key ? 'true' : 'false');
    });

    panel.querySelectorAll('[data-fontsize]').forEach(function (btn) {
      var key = parseInt(btn.getAttribute('data-fontsize'), 10);
      btn.setAttribute('aria-pressed', state.fontSize === key ? 'true' : 'false');
    });
  }

  function init() {
    buildWidget();
    applyState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
